import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const Dashboard = () => {
  const { user, logout, isAdmin, isVendor } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError('');
      const endpoint = isVendor() ? '/api/vendor/orders' : '/api/orders';
      const response = await axios.get(endpoint);
      // Our backend returns arrays directly, not wrapped in { data }
      setOrders(Array.isArray(response.data) ? response.data : (response.data.data || []));
    } catch (err) {
      setError('Failed to load dashboard data.');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const stats = {
    activeOrders: orders.filter(o => !['delivered', 'cancelled'].includes(o.status)).length,
    pendingApproval: orders.filter(o => o.status === 'draft').length,
    totalValue: orders.reduce((s, o) => s + (o.total_amount || 0), 0),
    recentUpdates: orders.filter(o => {
      const updated = new Date(o.updated_at);
      const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
      return updated > dayAgo;
    }).length
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = { draft: 'draft', confirmed: 'confirmed', production: 'production', shipped: 'shipped', delivered: 'delivered' };
    return statusMap[status?.toLowerCase()] || 'draft';
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const filteredOrders = statusFilter === 'all' ? orders : orders.filter(order => order.status?.toLowerCase() === statusFilter);

  return (
    <div className="dashboard">
      <header className="header">
        <div className="header-content">
          <div className="header-left">
            <div className="header-logo">TradeSync</div>
            <nav className="header-nav">
              <Link to="/dashboard" className="active">Dashboard</Link>
              {isAdmin() && <Link to="/vendors">Vendors</Link>}
              {isAdmin() && <Link to="/import" style={{ background: 'var(--primary)', color: '#fff', borderRadius: 8, padding: '6px 14px', fontWeight: 600 }}>+ Import Order</Link>}
            </nav>
          </div>
          <div className="header-right">
            <div className="user-info">
              <span className="user-badge">{user?.role}</span>
              <span>{user?.email}</span>
            </div>
            <button className="logout-btn" onClick={logout}>Logout</button>
          </div>
        </div>
      </header>

      <main className="dashboard-content">
        <div className="dashboard-header">
          <h2>{isAdmin() ? 'Command Center' : 'Vendor Dashboard'}</h2>
          <p>Welcome back! Here's what's happening with your orders.</p>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-card-header"><span className="stat-card-title">Active Orders</span><div className="stat-card-icon blue">📦</div></div>
            <div className="stat-card-value">{stats.activeOrders}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-header"><span className="stat-card-title">Pending Approval</span><div className="stat-card-icon orange">⏳</div></div>
            <div className="stat-card-value">{stats.pendingApproval}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-header"><span className="stat-card-title">Total Value</span><div className="stat-card-icon green">💰</div></div>
            <div className="stat-card-value">{formatCurrency(stats.totalValue)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-header"><span className="stat-card-title">Recent Updates</span><div className="stat-card-icon purple">🔄</div></div>
            <div className="stat-card-value">{stats.recentUpdates}</div>
          </div>
        </div>

        <div className="orders-section">
          <div className="orders-section-header">
            <h3 className="orders-section-title">{isAdmin() ? 'All Orders' : 'My Orders'}</h3>
            <div className="orders-filter">
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="confirmed">Confirmed</option>
                <option value="production">Production</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
              </select>
            </div>
          </div>
          {loading ? (
            <div className="loading-spinner">Loading orders...</div>
          ) : error ? (
            <div className="error-alert">{error}</div>
          ) : (
            <div className="orders-table-wrapper">
              <table className="orders-table">
                <thead>
                  <tr><th>Order #</th><th>Vendor</th><th>Status</th><th>Amount</th><th>Updated</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {filteredOrders.length === 0 ? (
                    <tr><td colSpan="6"><div className="empty-state"><div className="empty-state-icon">📋</div><h3>No orders found</h3></div></td></tr>
                  ) : filteredOrders.map((order) => (
                    <tr key={order.id}>
                      <td><strong>{order.order_number || order.id}</strong></td>
                      <td>{order.vendor_name || '-'}</td>
                      <td><span className={`status-badge ${getStatusBadgeClass(order.status)}`}>{order.status || 'Draft'}</span></td>
                      <td>{formatCurrency(order.total_amount)}</td>
                      <td>{formatDate(order.updated_at)}</td>
                      <td><Link to={`/orders/${order.id}`} className="action-link">View →</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
